import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StoreInfoForm = ({ storeData, onUpdate, onSave, isDraft, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    description: '',
    category: '',
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      // Add other social media fields if needed
    },
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (storeData) {
      setFormData({
        name: storeData.name || '',
        email: storeData.email || '',
        phone: storeData.phone || '',
        address: storeData.address || '',
        website: storeData.website || '',
        description: storeData.description || '',
        category: storeData.category || '',
        socialMedia: {
          facebook: storeData.facebook || '',
          instagram: storeData.instagram || '',
          twitter: storeData.twitter || '',
          linkedin: storeData.linkedin || '',
        },
      });
    }
  }, [storeData]);

  const categoryOptions = [
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'retail', label: 'Retail Store' },
    { value: 'service', label: 'Service Business' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'beauty', label: 'Beauty & Wellness' },
    { value: 'technology', label: 'Technology' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onUpdate(updatedData);
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSocialMediaChange = (platform, value) => {
    const updatedSocial = {
      ...formData.socialMedia,
      [platform]: value
    };
    const updatedData = { ...formData, socialMedia: updatedSocial };
    setFormData(updatedData);
    onUpdate(updatedData);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) newErrors.name = 'Store name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData?.email)) newErrors.email = 'Invalid email format';
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData?.address?.trim()) newErrors.address = 'Address is required';
    if (!formData?.category) newErrors.category = 'Business category is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (asDraft = false) => {
    if (!asDraft && !validateForm()) return;
    onSave(formData, asDraft);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Store" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Store Information</h2>
              <p className="text-sm text-muted-foreground">Manage your business details and settings</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {isDraft && (
              <span className="px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded-md">
                Draft Mode
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSave(true)}
              iconName="Save"
              iconPosition="left"
              disabled={isLoading}
            >
              Save Draft
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => handleSave(false)}
              iconName="Upload"
              iconPosition="left"
              loading={isLoading}
            >
              Publish
            </Button>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-8">
        {/* Basic Information */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-foreground flex items-center space-x-2">
            <Icon name="Building2" size={20} />
            <span>Basic Information</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Store Name"
              type="text"
              placeholder="Enter store name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />
            
         
          </div>

          <Input
            label="Business Description"
            type="text"
            placeholder="Describe your business in detail"
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            description="This will be displayed to customers on your store page"
          />
        </div>

        {/* Contact Information */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-foreground flex items-center space-x-2">
            <Icon name="Phone" size={20} />
            <span>Contact Information</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Email Address"
              type="email"
              placeholder="business@example.com"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />
            
            <Input
              label="Phone Number"
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              error={errors?.phone}
              required
            />
          </div>

          <Input
            label="Business Address"
            type="text"
            placeholder="123 Main Street, City, State, ZIP"
            value={formData?.address}
            onChange={(e) => handleInputChange('address', e?.target?.value)}
            error={errors?.address}
            required
          />

          <Input
            label="Website URL"
            type="url"
            placeholder="https://www.yourstore.com"
            value={formData?.website}
            onChange={(e) => handleInputChange('website', e?.target?.value)}
          />
        </div>

        {/* Social Media */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-foreground flex items-center space-x-2">
            <Icon name="Share2" size={20} />
            <span>Social Media Links</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Facebook"
              type="url"
              placeholder="https://facebook.com/yourstore"
              value={formData?.socialMedia?.facebook}
              onChange={(e) => handleSocialMediaChange('facebook', e?.target?.value)}
            />
            
            <Input
              label="Instagram"
              type="url"
              placeholder="https://instagram.com/yourstore"
              value={formData?.socialMedia?.instagram}
              onChange={(e) => handleSocialMediaChange('instagram', e?.target?.value)}
            />
            
            <Input
              label="Twitter"
              type="url"
              placeholder="https://twitter.com/yourstore"
              value={formData?.socialMedia?.twitter}
              onChange={(e) => handleSocialMediaChange('twitter', e?.target?.value)}
            />
            
            <Input
              label="LinkedIn"
              type="url"
              placeholder="https://linkedin.com/company/yourstore"
              value={formData?.socialMedia?.linkedin}
              onChange={(e) => handleSocialMediaChange('linkedin', e?.target?.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreInfoForm;
